import { Provider } from '@angular/core';
import { DateTimeAdapter, OWL_DATE_TIME_FORMATS, OWL_DATE_TIME_LOCALE } from 'ng-pick-datetime-ex';
import { HelperService } from './app/shared/service/helper.service';
import { MomentDateTimeAdapter } from 'ng-pick-datetime-moment';
import { CustomDateFormat } from './app/store/user/model';
import { IPage } from './constants.model';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { DatatableOutputSortInterface } from './app/shared/component/datatable/datatable.model';
import { Moment } from 'moment';

const PORTAL_TOOL_NAME: string = 'IoT Device Management Portal';
export const BROWSER_TITLE_SUFFIX: string = ` | ${PORTAL_TOOL_NAME}`;
export const RADIX: number = 10;
export const STATIC_MAX_LIMIT_OF_CRUD = 500;
export function setLocale(helperService: HelperService): string {
  return helperService.setLocale();
}
export function setCustomDateFormats(helperService: HelperService): CustomDateFormat {
  return helperService.setCustomDateFormats();
}
export const OWL_DATE_TIME_PROVIDERS: Provider[] = [
  { provide: OWL_DATE_TIME_LOCALE, deps: [HelperService], useFactory: setLocale },
  { provide: DateTimeAdapter, useClass: MomentDateTimeAdapter, deps: [OWL_DATE_TIME_LOCALE] },
  { provide: OWL_DATE_TIME_FORMATS, deps: [HelperService], useFactory: setCustomDateFormats },
];

export enum ActiveStatuses {
  ACTIVE = 1,
  INACTIVE = 0,
}

export enum StatusesEnum {
  ACTIVE = 1,
  INACTIVE = 2,
}



export enum CountEntryLanguageKeys {
  CUMULATIVE = 'lines.excel.countEntryMethod.cumulative',
  INCREMENTAL = 'lines.excel.countEntryMethod.incremental',
}


export enum TrueFalse {
  True = 1,
  False = 0,
}

export enum YesNoEnum {
  YES = 1,
  NO = 0,
}


const DEFAULT_ICON: string = 'icon-layout-sidebar-left';
const EDIT_ICON: string = 'fas fa-edit';
const COGS_ICON: string = 'fas fa-cogs';
const CALENDAR_ICON: string = 'fa fa-calendar';
const CALENDAR_ALT_ICON: string = 'fas fa-calendar-alt';
const CUBE_ICON: string = 'fas fa-cube';
const COG_ICON: string = 'fa fa-cog';
const USER_CLOCK_ICON: string = 'fas fa-user-clock';
const COPY_ICON: string = 'fa fa-copy fa-flip-horizontal';
const BARCODE_ICON: string = 'fas fa-barcode';
const LIST_ICON: string = 'fas fa-list-alt';
const FILE_SIGN_ICON: string = 'fas fa-file-signature';
const HOURGLASS_HALF_ICON: string = 'fas fa-hourglass-half';
const FILE_ALT_ICON: string = 'fas fa-file-alt';
const FILE_ICON: string = 'fas fa-file-alt';
const BAR_CHART_ICON: string = 'fa fa-bar-chart';

export const smallModal: NgbModalOptions = {
  keyboard: false,
  backdrop: 'static',
  windowClass: 'scw-modal-sm',
};

export const mediumModal: NgbModalOptions = {
  keyboard: false,
  backdrop: 'static',
  windowClass: 'scw-modal-md',
};

export const PAGES: IPage[] = [
  {
    title: 'Home',
    titleKey: 'home',
    icon: DEFAULT_ICON,
    relatedModule: 'home',
  },
  {
    title: 'Device Management',
    titleKey: 'device-management',
    icon: DEFAULT_ICON,
    relatedModule: 'device-management',
  },
  {
    title: 'Device Dashboard',
    titleKey: 'device-dashboard',
    icon: DEFAULT_ICON,
    relatedModule: 'device-dashboard',
  },
  {
    title: 'Device Last Messages',
    titleKey: 'device-last-message',
    icon: DEFAULT_ICON,
    relatedModule: 'device-last-message',
  },
  {
    title: 'Device Command Schedule',
    titleKey: 'device-command-schedule',
    icon: DEFAULT_ICON,
    relatedModule: 'device-command-schedule',
  },
  {
    title: 'Firmware Manager',
    titleKey: 'firmware-manager',
    icon: FILE_SIGN_ICON,
    relatedModule: 'firmware-manager',
  },
];

export enum ToastTypeEnum {
  success = 'success',
  error = 'error',
  info = 'info',
  warning = 'warning',
}

export enum MultipleCheckInOptionsForSource {
  No = 0,
  Yes = 1,
  SiteDefault = 2,
}

export const unitTypes = {
  4: 'products.lookups.unit.unitGram',
  5: 'products.lookups.unit.unitKilogram',
  6: 'products.lookups.unit.unitLiter',
  7: 'products.lookups.unit.unitMeter',
  8: 'products.lookups.unit.unitCentimeter',
  9: 'products.lookups.unit.unitPiece',
};

export const genericModalOptions: NgbModalOptions = {
  size: 'lg',
  windowClass: 'full-modal',
  keyboard: false,
  backdrop: 'static',
};

export enum GenericModalTypes {
  add = 'add',
  edit = 'edit',
  bulkEdit = 'bulkEdit',
  delete = 'delete',
}

export enum SensorManufacturerEnum {
  MONNIT = 'monnit',
  SCW = 'scw',
  AUTO_COUNTER = 'autocounter',
}

export enum SensorTypeEnum {
  GOOD_COUNTER = 'good_counter',
  MID_COUNTER = 'mid_counter',
  TOTAL_COUNTER = 'total_counter',
  VIBRATION = 'vibration',
  VOLTAGE = 'voltage',
  CARD_READER = 'card_reader',
  SUB_SENSOR = 'sub_sensor',
}

export enum SensorDataType {
  DAY = 'day',
  HOUR = 'hour',
}

export const sensorTypeTranslations = {
  [SensorTypeEnum.GOOD_COUNTER]: 'sensorTypes.sensor_type_good_counter',
  [SensorTypeEnum.MID_COUNTER]: 'sensorTypes.sensor_type_mid_counter',
  [SensorTypeEnum.TOTAL_COUNTER]: 'sensorTypes.sensor_type_total_counter',
  [SensorTypeEnum.VIBRATION]: 'sensorTypes.sensor_type_vibration',
  [SensorTypeEnum.VOLTAGE]: 'sensorTypes.sensor_type_voltage',
  [SensorTypeEnum.CARD_READER]: 'sensorTypes.sensor_type_card_reader',
  [SensorTypeEnum.SUB_SENSOR]: 'sensorTypes.sensor_type_sub_sensor',
};

export const defaultPageSort: DatatableOutputSortInterface = {
  type: 'descending',
  column: 'id',
};

export type DateRanges = {
  [key in 'today'
    | 'yesterday'
    | 'thisWeek'
    | 'lastWeek'
    | 'thisMonth'
    | 'lastMonth'
    | 'thisYear'
    | 'lastYear']?: [Moment, Moment];
};

export enum ELanguages {
  EN = 'en',
  TR = 'tr',
  DE = 'de',
  ES = 'es',
  FR = 'fr',
  IT = 'it',
  PL = 'pl',
}

export enum EOtherLanguages {
  TR = 'tr',
  DE = 'de',
  ES = 'es',
  FR = 'fr',
  IT = 'it',
  PL = 'pl',
}

export type TLanguageOptions = {
  [key in ELanguages]?: string;
};

export type TOtherLanguageOptions = {
  [key in EOtherLanguages]: string;
};

export const SIGNED_INT_11_MAX_VALUE = 2147483647;
export const MYSQL_MAX_TEXT_LENGTH = 65535;
export const MYSQL_DEFAULT_VARCHAR_LENGTH = 255;
export const DEFAULT_CHECK_IN_PIN = '1234';
